import type { Customer } from '../../../shared/models/customerModel';
import {
  JOURNEY_STAGE_VALUES,
  type CustomerDashboardFields,
  type JourneyStage,
  type OnboardingDashboardCustomer,
  type SuluStatus,
} from '../../../shared/models/onboardingDashboard.types';
import { normalizeSuluStatus } from '../../../shared/utils/customerApiNormalize';

export const STAGE_COLUMNS: {
  id: JourneyStage;
  label: string;
  columnOwner: string;
}[] = [
  { id: 'request', label: 'Request', columnOwner: 'Product Team' },
  { id: 'domainApproval', label: 'Approval', columnOwner: 'Ops' },
  { id: 'domainCreate', label: 'Domain', columnOwner: 'DevOps' },
  { id: 'masterData', label: 'Master data', columnOwner: 'Product Team' },
  { id: 'complete', label: 'Complete', columnOwner: '' },
];

export const isJourneyStage = (value: string): value is JourneyStage =>
  (JOURNEY_STAGE_VALUES as readonly string[]).includes(value);

/** Resolve UX stage from onboardingStatus (journey value) or legacy Pending/DomainSetup/… + domain status. */
export const resolveJourneyStage = (customer: Customer): JourneyStage => {
  if (isJourneyStage(customer.onboardingStatus)) {
    return customer.onboardingStatus;
  }

  const { onboardingStatus, domainAprrovalStatus } = customer;

  if (domainAprrovalStatus === 'Rejected') {
    return 'request';
  }
  if (onboardingStatus === 'Completed') {
    return 'complete';
  }
  if (
    onboardingStatus === 'DataEntry' ||
    onboardingStatus === 'DataSetup' ||
    onboardingStatus === 'Feedback'
  ) {
    return 'masterData';
  }
  if (onboardingStatus === 'DomainSetup') {
    return 'domainCreate';
  }
  if (
    domainAprrovalStatus === 'DomainSubmittedToDevops' ||
    domainAprrovalStatus === 'WaitingforDomaincreation' ||
    domainAprrovalStatus === 'Pending'
  ) {
    return 'domainApproval';
  }
  if (domainAprrovalStatus === 'Waitingforsubmission') {
    return 'request';
  }
  if (domainAprrovalStatus === 'WaitingForDataEntry') {
    return 'masterData';
  }
  if (domainAprrovalStatus === 'Approved') {
    // Completed / DataEntry / DataSetup / Feedback were already handled above.
    return 'domainCreate';
  }

  return 'request';
};

const stageMeta: Record<
  JourneyStage,
  Pick<CustomerDashboardFields, 'onboardingStatusLabel' | 'stageColumnOwner'>
> = {
  request: { onboardingStatusLabel: 'Request', stageColumnOwner: 'Product Team' },
  domainApproval: { onboardingStatusLabel: 'Approval', stageColumnOwner: 'Ops' },
  domainCreate: { onboardingStatusLabel: 'Domain', stageColumnOwner: 'DevOps' },
  masterData: { onboardingStatusLabel: 'Master data', stageColumnOwner: 'Product Team' },
  complete: { onboardingStatusLabel: 'Complete', stageColumnOwner: '' },
};

const nextByStage: Record<
  JourneyStage,
  Pick<CustomerDashboardFields, 'nextAction' | 'nextActionOwner'>
> = {
  request: { nextAction: 'Resubmit for approval', nextActionOwner: 'Product Team' },
  domainApproval: { nextAction: 'Approve domain request', nextActionOwner: 'Ops' },
  domainCreate: { nextAction: 'Create domain in OTM', nextActionOwner: 'DevOps' },
  masterData: { nextAction: 'Enter master data', nextActionOwner: 'Product Team' },
  complete: { nextAction: 'Onboarding complete', nextActionOwner: 'No owner' },
};

const deriveSulu = (
  stage: JourneyStage
): Pick<CustomerDashboardFields, 'suluStatus' | 'suluStatusLabel'> => {
  if (stage === 'complete') {
    return { suluStatus: 'complete', suluStatusLabel: 'Complete' };
  }
  if (stage === 'masterData') {
    return { suluStatus: 'inProgress', suluStatusLabel: 'In progress' };
  }
  if (stage === 'domainCreate') {
    return { suluStatus: 'needsPartnerMaster', suluStatusLabel: 'Needs Partner Master' };
  }
  return { suluStatus: 'notStarted', suluStatusLabel: 'Not started' };
};

const hasDashboardFields = (customer: Customer): customer is OnboardingDashboardCustomer =>
  'nextAction' in customer && typeof (customer as OnboardingDashboardCustomer).nextAction === 'string';

export const getOnboardingStatusLabel = (customer: Customer): string => {
  const enriched = customer as OnboardingDashboardCustomer;
  if (enriched.onboardingStatusLabel) {
    return enriched.onboardingStatusLabel;
  }
  return stageMeta[resolveJourneyStage(customer)].onboardingStatusLabel ?? customer.onboardingStatus;
};

export const enrichCustomerForDashboard = (customer: Customer): OnboardingDashboardCustomer => {
  const stage = resolveJourneyStage(customer);
  const meta = stageMeta[stage];
  const next = nextByStage[stage];
  const sulu = deriveSulu(stage);

  if (hasDashboardFields(customer)) {
    const normalizedSulu =
      normalizeSuluStatus(customer.suluStatus) ?? sulu.suluStatus;
    return {
      ...customer,
      onboardingStatusLabel: customer.onboardingStatusLabel ?? meta.onboardingStatusLabel,
      stageColumnOwner: customer.stageColumnOwner ?? meta.stageColumnOwner,
      suluStatus: normalizedSulu,
      suluStatusLabel: customer.suluStatusLabel ?? sulu.suluStatusLabel,
      isRejected: customer.isRejected ?? customer.domainAprrovalStatus === 'Rejected',
    };
  }

  return {
    ...customer,
    onboardingStatusLabel: meta.onboardingStatusLabel,
    stageColumnOwner: meta.stageColumnOwner,
    nextAction: next.nextAction,
    nextActionOwner: next.nextActionOwner,
    isRejected: customer.domainAprrovalStatus === 'Rejected',
    ...sulu,
  };
};

export const enrichCustomersForDashboard = (customers: Customer[]): OnboardingDashboardCustomer[] =>
  customers.map(enrichCustomerForDashboard);

export const suluChipColors: Record<SuluStatus, { bg: string; color: string }> = {
  notStarted: { bg: '#f7f7f7', color: '#2b2b2b' },
  needsPartnerMaster: { bg: '#ffefd1', color: '#4e380e' },
  inProgress: { bg: '#eff7ff', color: '#00528d' },
  complete: { bg: '#edf8f4', color: '#008655' },
  failed: { bg: '#fdecea', color: '#c62828' },
};

export const getSuluChipColors = (status: SuluStatus | string | undefined) => {
  const key = normalizeSuluStatus(status) ?? 'notStarted';
  return suluChipColors[key] ?? suluChipColors.notStarted;
};

export const journeyStageChipColors: Record<JourneyStage, { bg: string; color: string }> = {
  request: { bg: '#f7f7f7', color: '#2b2b2b' },
  domainApproval: { bg: '#ffefd1', color: '#4e380e' },
  domainCreate: { bg: '#eff7ff', color: '#00528d' },
  masterData: { bg: '#eff7ff', color: '#00528d' },
  complete: { bg: '#edf8f4', color: '#008655' },
};
